import { Injectable } from '@angular/core';
import StorageType from '../models-internal/enums/storage-type.enum';
import { Subject } from 'rxjs';

//https://www.learnrxjs.io/learn-rxjs/operators/multicasting/share
import { share } from 'rxjs/operators';
import { LogService } from './log.service';

/**
 * Service that manages both local and session storage in the browser application
 */
@Injectable({
  providedIn: "root",
})
export class AppStorageService {
  private readonly _componentName = 'AppStorageService';

  /**
   * Keys that have been used in the past to store data in local storage,
   * but now should be stored in session storage.  This functions as a safety net
   * in case the component code statements are missed.
   */
  private localStorageRedirectKeys = ['current-landowner-agreement', 'initialQueryParams', 'initialUrl'];

  private localStorageSubject = new Subject<{ key: string; value: any }>();

  /**
   * Emits when local storage changes
   */
  public localChanges = this.localStorageSubject.asObservable().pipe(share());

  constructor(private log: LogService) {}

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  public setItem(key: string, value: string, storageType: StorageType) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.setItem, storageType=${storageType}, key=${key}, value=`, value);
    }

    if (this.localStorageRedirectKeys.includes(key)) {
      storageType = StorageType.Session;
    }

    switch (storageType) {
      case StorageType.Local:
        localStorage.setItem(key, value);

        this.localStorageSubject.next({ key, value });

        break;

      case StorageType.Session:
        sessionStorage.setItem(key, value);
        break;
      default:
        console.error(`${this._componentName}.setItem - Storage Type Not Supported:`, storageType);
        break;
    }
  }

  public setSessionItem(key: string, value: string) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.setSessionItem, key=${key}, value=`, value);
    }

    return this.setItem(key, value, StorageType.Session);
  }

  public getItem(key: string, storageType: StorageType) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.getItem, storageType=${storageType}, key=${key}`);
    }

    if (this.localStorageRedirectKeys.includes(key)) {
      storageType = StorageType.Session;
    }

    switch (storageType) {
      case StorageType.Local:
        return localStorage.getItem(key);

        break;

      case StorageType.Session:
        return sessionStorage.getItem(key);
        break;

      default:
        console.error(`${this._componentName}.getItem - Storage Type Not Supported:`, storageType);
        break;
    }
  }

  public getSessionItem(key: string) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.getSessionItem, key=${key}`);
    }

    return this.getItem(key, StorageType.Session);
  }

  public removeItem(key: string, storageType: StorageType) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.removeItem, key=${key}, storageType:${storageType}`);
    }

    if (this.localStorageRedirectKeys.includes(key)) {
      storageType = StorageType.Session;
    }

    switch (storageType) {
      case StorageType.Local:
        localStorage.removeItem(key);
        this.localStorageSubject.next({ key, value: null });
        break;

      case StorageType.Session:
        sessionStorage.removeItem(key);
        break;

      default:
        console.error(`${this._componentName}.removeItem - Storage Type Not Supported:`, storageType);
        break;
    }
  }
}
